/**
 * channels-service-helpers
 * ------------------------
 * Consolidated helpers for the channels-service + Storyblok flag-ON integration
 * (`USE_CHANNELS_SERVICE`). These were previously one file per function under
 * `lib/helper/`; merged into this single module so the integration's utilities
 * are easy to find and import. Grouped by concern:
 *
 *   1. Feature flag        — isChannelsServiceEnabled
 *   2. Source selection    — pickChannels, pickChrome
 *   3. Lineup packages     — deriveSignaturePackages, deriveGenrePackages
 *   4. Lineup config       — buildLineupConfig
 *   5. Platform pick       — pickPlatform, platformOf (+ PlatformKey)
 *   6. Category bucketing  — bucketChannelsByCategory, isChannelInAnyCategory
 *   7. Category ordering   — structuralCategoryKey, orderChannelCategoryConfig
 *   8. FAST-channel filter — isExcludedFastChannel (+ FastChannelFlagged)
 *   9. MyHomeTeam overlay  — overlayMyHomeTeamRsns
 */

import type { ChannelListingEntry } from "../adapters/channels-service/channel-listing-adapter";
import { envConstants } from "../constant/env-constant";

/* ════ 1. Feature flag ═══════════════════════════════════════════════════ */

/**
 * Gate for the channels-service + Storyblok migration. When TRUE, channels come
 * from the channels-service and chrome/CCT config from Storyblok; when FALSE the
 * legacy AEM-fetched path serves both.
 *
 * The env getter returns a raw string ("TRUE"/"FALSE"/undefined), so the
 * `=== "TRUE"` check is centralized here — `if (envConstants.USE_X)` would be
 * truthy even for "FALSE". Source: `config/env-config.json`.
 */
export function isChannelsServiceEnabled(): boolean {
  return envConstants.USE_CHANNELS_SERVICE === "TRUE";
}

/* ════ 2. Source selection (the flag switch, picked at each consumer) ═════ */

/**
 * THE flag switch for CHANNELS, picked at the consumer.
 *
 *   - flag ON  + service has channels -> channels-service channels
 *   - flag ON  + service empty/down   -> AEM channels (WAT-9902 degraded fallback)
 *   - flag OFF                        -> AEM channels (legacy)
 *
 * The `.length` check (not `??`) is mandatory: the service can advertise READY
 * with 0 channels, and `[]` is not nullish — a `service ?? aem` would render an
 * empty grid instead of falling back. Returns the array directly (no envelope
 * wrapper); `aemChannels` is the raw AEM listing, always.
 */
export function pickChannels(
  serviceChannels: ChannelListingEntry[] | undefined,
  aemChannels: ChannelListingEntry[] | undefined
): ChannelListingEntry[] {
  if (isChannelsServiceEnabled() && serviceChannels?.length) {
    return serviceChannels;
  }
  return aemChannels ?? [];
}

/**
 * Returns true when the channels-service flag is ON AND at least one of the
 * supplied channel arrays is non-empty (i.e. the service is supplying channels
 * on at least one platform). This is the "Option C" gate used by adapters to
 * decide whether category bucketing follows the prose package-config blok or
 * the AEM slug-keyed channelCategories.
 *
 * Pass every platform's channel array that participates in the decision:
 *   - lineup / interactive-tool: one arg (OTT only)
 *   - compare tool: two args (OTT OR satellite)
 *
 * Mirrors `pickChannels`'s `.length` semantics — an empty `[]` from a degraded
 * healthy-but-empty feed is treated as absent, falling through to AEM.
 */
export function isUsingServiceChannels(...sources: (any[] | undefined)[]): boolean {
  return isChannelsServiceEnabled() && sources.some(s => s?.length);
}

/**
 * THE flag switch for CHROME (packages / categories / miniPacks / sortOptions …).
 *
 *   - flag ON  -> the package-config value (falls back to AEM if missing)
 *   - flag OFF -> the AEM value
 *
 * No `.length` rule here — chrome has no "READY but empty" degraded state, so a
 * nullish fallback is correct.
 */
export function pickChrome<T>(serviceChrome: T | undefined, aemChrome: T): T {
  return isChannelsServiceEnabled() ? (serviceChrome ?? aemChrome) : aemChrome;
}

/* ════ 3. Lineup packages (derived from the package-config catalog) ══════ */

/**
 * Derive channel-lineup `packages[]` and `genrePackages[]` from the global
 * package-config catalog (`sales/www/global/package-config`) instead of
 * authoring per-page package objects. The catalog already carries everything
 * the package cards render: `displayLabel`, channel `count`, the per-platform
 * Shop CTA url (`cta[0].targeturl`), `price`, `order` and a `type` discriminator.
 *
 * Output shape matches what `parsePackagesData` reads (packageId, categoryId,
 * numOfChannels, ufCTA/gpCta, packageConfig). `packageConfig` is the raw catalog
 * entry, so downstream `packageConfig.{displayLabel,order,queryparam,price}`
 * reads resolve unchanged.
 */

// Catalog `type` values that classify a package.
const SIGNATURE_TYPES = ["internet", "satellite"];
const GENRE_TYPES = ["genre", "genre-mini"];

// Spanish/World tier ids — the catalog has no English/Spanish flag, so derive
// categoryId from the id. Everything else is treated as ENGLISH (the default
// tab; `parsePackagesData` skips any package missing a categoryId).
const SPANISH_ID = /MAS_LATINO|OPTIMO_MAS|MAS_ULTRA|LO_MAXIMO|MIESPANOL/;

const ctaOf = (entry: any) => {
  const cta = Array.isArray(entry?.cta) ? entry.cta[0] : entry?.cta;
  return cta?.targeturl ? { ctaUrl: cta.targeturl, cta: cta.label } : undefined;
};

const countOf = (entry: any) => {
  const ch = Array.isArray(entry?.channel) ? entry.channel[0] : entry?.channel;
  const count = (ch?.count ?? "").toString().trim();
  if (!count) return "";
  const label = (ch?.label ?? "").toString().trim();
  // Bare numeric counts render "90+ channels"; leave pre-formatted counts as-is.
  const plus = /^\d+$/.test(count) ? "+" : "";
  return label ? `${count}${plus} ${label}` : `${count}${plus}`;
};

// Genre-pack icon (rendered by IconCategory via `packageData.icon`). Tolerates a
// plain URL string, a Storyblok asset object, or a single-element array.
const iconOf = (entry: any): string | undefined => {
  const raw = Array.isArray(entry?.icon) ? entry.icon[0] : entry?.icon;
  if (!raw) return undefined;
  return typeof raw === "string" ? raw : (raw.filename || raw.url || undefined);
};

const mapEntry = (entry: any, isGenre: boolean) => {
  const cta = ctaOf(entry);
  const icon = isGenre ? iconOf(entry) : undefined;
  // `packageNameLabel` is the canonical name source for BOTH consumers of these
  // packages: the lineup page's parsePackagesData AND the compare-tool MODAL's DDS
  // ChannelLineupProvider rebuild `pkg.name` from it (name = packageNameLabel ||
  // packageNameLabelSystem), ignoring any pre-set `name`. Genre packs key off the id
  // (ATT_TV_MYSPORTS -> MYSPORTS, also matching IconCategory's
  // bannerPackages.find(pkg => pkg.name === categoryKey)); signature tiers MUST use
  // displayLabel ("ENTERTAINMENT"/"CHOICE™"). If left undefined the provider yields
  // name=undefined -> the modal's package-QP validity check (getArePackageQPsValid:
  // pkg.name === cmprPkg) fails -> the whole comparison grid is replaced by the
  // PdfLinksErrorDisplay fallback (getPackageColors is NOT the cause; the grid never
  // mounts).
  const packageNameLabel = isGenre
    ? (entry?.id ?? "").replace(/^ATT_TV_/, "")
    : entry?.displayLabel;
  // Top-level `name` too, for AEM-shape parity / any raw consumer before re-derive.
  const name = entry?.displayLabel || packageNameLabel;
  return {
    packageId: entry?.id,
    categoryId: SPANISH_ID.test(entry?.id ?? "") ? "SPANISH" : "ENGLISH",
    numOfChannels: countOf(entry),
    packageConfig: entry,
    ...(name && { name }),
    ...(cta && { ufCTA: cta }),
    ...(isGenre && cta && { gpCta: cta }),
    ...(icon && { icon }),
    ...(packageNameLabel && { packageNameLabel }),
    // Header tooltip — package-config stores a plain string; wrap as { content }
    // to match the FusionTooltipProps shape the lineup renders (= prod/AEM shape;
    // the parser gates on p.tooltip.content, the ToolTip parseHtml's that string).
    // Re-homes the AEM-only field for flag-ON (like icon/packageNameLabel above).
    ...(entry?.tooltip && { tooltip: { content: entry.tooltip } }),
    // billingCodeLabel re-homed for flag-ON parity (AEM packages carry it top-level;
    // package-config keys it as `queryparam`). The compare modal's SVOD→package join
    // (ChnlComparisonGrid embeddedSvodsData) reads pkg.billingCodeLabel, which the DDS
    // getFromCmsFeed copies verbatim with no packageConfig fallback — without it the
    // "Included subscriptions" section is dropped flag-ON.
    ...(entry?.queryparam && { billingCodeLabel: entry.queryparam }),
  };
};

/**
 * Signature lineup packages (Entertainment/Choice/Ultimate/Premier + Spanish
 * tiers), both platforms. Returned whole; the lineup adapter prefix-filters to
 * the route's platform (ATT_TV_* stream / DTV_* satellite).
 */
export const deriveSignaturePackages = (catalog: any[]): any[] =>
  (catalog ?? [])
    .filter((p) => SIGNATURE_TYPES.includes(p?.type))
    .map((p) => mapEntry(p, false));

/** Genre packs (MySports/MyKids/… + the mini packs). */
export const deriveGenrePackages = (catalog: any[]): any[] =>
  (catalog ?? [])
    .filter((p) => GENRE_TYPES.includes(p?.type))
    .map((p) => mapEntry(p, true));

/**
 * Canonical join map: `billingProductCode` -> packageId (the catalog `id`).
 * The channels-service adapter resolves each feed PackageRef to its catalog id
 * by EXACT billingProductCode match (e.g. `BOLT-MYNEWS-202410` ->
 * `ATT_TV_MYNEWS_LOCALS`) — NOT a substring/includes scan — replacing the
 * brittle name -> `ATT_TV_<SNAKE>` derivation for renamed & combo packs the name
 * alias can't reach. Type-agnostic: ONE map covers signature, genre and
 * satellite entries (do not pre-filter by `type`). Entries without a
 * billingProductCode (overlay-only packs like MyHomeTeam, or not-yet-authored)
 * are skipped — those fall back to the name alias + the lineup parser's chain.
 * First-write wins on the (expected-unique) product code.
 */
export const buildBillingProductCodeMap = (catalog: any[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const entry of catalog ?? []) {
    const code = entry?.billingProductCode;
    const id = entry?.id;
    if (typeof code === "string" && code && typeof id === "string" && id && !map.has(code)) {
      map.set(code, id);
    }
  }
  return map;
};

/* ════ 4. Lineup config (globalLineupConfig builder) ═════════════════════ */

/**
 * THE single builder for `globalLineupConfig` — the dedicated chrome
 * (packages / miniPacks / categories …) prop that every channel consumer reads
 * via `pickChrome`, so the AEM-composed `channelListingData` object can
 * eventually be deleted. (sortOptions/defaultSortOptions are deliberately
 * excluded — they're per-surface story fields, not shared chrome.)
 *
 * Sourced entirely from the global package-config feed
 * (`sales/www/global/package-config`):
 *   - `packagesConfigFeed` carries the chrome arrays under un-prefixed keys
 *     (`parseGlobalSBFeed`'s `cleanEmpty` strips the `array_` authoring prefix).
 *     The feed delivers bare CSV arrays, but the adapters read `.options`, so
 *     `wrapOptions` boxes a bare array into `{ options: [...] }` (no-op if it is
 *     already wrapped or not an array).
 *   - `packagesConfig` is the 24-entry package catalog
 *     (`packagesConfigFeed.packageConfig`); `packages` / `genrePackages` are
 *     derived from it by `type`, not authored.
 *
 * This builder is flag-AGNOSTIC: the caller decides whether to invoke it
 * (gated on `isChannelsServiceEnabled()`), so under flag OFF the consumers keep
 * `globalLineupConfig === undefined` and every read falls through to the AEM
 * (`content.*`) path unchanged.
 */
export const buildLineupConfig = (
  packagesConfigFeed: any,
  packagesConfig: any
) => {
  const wrapOptions = (v: any) => (Array.isArray(v) ? { options: v } : v);
  return {
    categoryConfig: packagesConfigFeed?.channelCategories,
    miniPacks: wrapOptions(packagesConfigFeed?.miniPacks),
    packageCategories: wrapOptions(packagesConfigFeed?.packageCategories),
    // sortOptions / defaultSortOptions are intentionally NOT sourced here: each
    // channel-lineup surface (stream/satellite page + compare-tool modal) needs
    // DIFFERENT sort options, so they're read per-surface from each story's own
    // fields (channelLineupInteractiveTool / channelComparisonTool blok), not the
    // shared package-config feed. See channel-lineup-page-adapter +
    // channel-compare-tool-page-adapter.
    genreChannelCategories: wrapOptions(packagesConfigFeed?.genreChannelCategories),
    packages: deriveSignaturePackages(packagesConfig),
    genrePackages: deriveGenrePackages(packagesConfig),
    commonData: {
      channelIncludedText: packagesConfigFeed?.channelIncludedText,
      channelAddOntext: packagesConfigFeed?.channelAddOntext,
      channelPromoText: packagesConfigFeed?.channelPromoText,
      localBadgeTooltip: packagesConfigFeed?.localBadgeTooltip,
      rsnBadgeTooltip: packagesConfigFeed?.rsnBadgeTooltip,
    },
  };
};

/* ════ 5. Platform pick (SB story per-platform field) ════════════════════ */

/**
 * Platform-variant field picker for the channel-lineup SB story. Unifies two
 * shapes a field can arrive as:
 *   - sibling-keyed `{ stream: ..., satellite: ... }` (a per-platform SB story)
 *   - flat value (legacy AEM content, already platform-specific because stream
 *     and satellite have separate AEM files)
 * If `obj` is sibling-keyed, return the active platform's value; else return
 * `obj` as-is. Same code path works flag-ON and flag-OFF.
 *
 * Vocabulary note: the keys here (`"stream" | "satellite"`) describe the SB
 * story's per-route layout/labels and are INDEPENDENT of the channels-service
 * REST `Platform` type (`"ott" | "satellite" | "business"`). Do not conflate.
 */
export type PlatformKey = "stream" | "satellite";

/**
 * @param obj  A platform-keyed object `{stream, satellite}` or a flat value.
 * @param platform Active platform, derived from `isSatelliteChlup` in adapters.
 * @returns The platform-specific value when `obj` has sibling keys; `obj` as-is
 *          otherwise. Returns `undefined` only when input is null/undefined.
 */
export function pickPlatform<T = any>(obj: any, platform: PlatformKey): T {
  if (obj == null) return obj;
  if (typeof obj === "object" && !Array.isArray(obj)) {
    // Sibling-keys shape: { stream: ..., satellite: ... }
    if (obj.stream !== undefined || obj.satellite !== undefined) {
      const variant = obj[platform];
      // Fall back to whichever sibling is defined if the requested one is missing.
      return (variant !== undefined ? variant : (obj.stream ?? obj.satellite)) as T;
    }
  }
  // Flat shape (legacy AEM-derived or platform-agnostic content).
  return obj as T;
}

/**
 * Convenience: derives the platform key from the adapter's `isSatelliteChlup`
 * flag. Centralizes the boolean→string translation so adapters don't repeat it.
 */
export function platformOf(isSatelliteChlup: boolean | undefined): PlatformKey {
  return isSatelliteChlup ? "satellite" : "stream";
}

/* ════ 6. Category bucketing (channel → accordion) ═══════════════════════ */

export interface ChannelCategoryConfig {
  label: string;
  engCategories?: string[];
  /**
   * KV shape (flag-OFF / structural entries): the scalar category key the
   * channel's own `category` field is matched against. Coexists with the blok
   * shape so one config array can mix both.
   */
  key?: string;
  value?: string;
}

/**
 * Minimal lineup-channel shape these helpers read. This is the enriched
 * channel-listing row (NOT the adapter's `ChannelListingEntry` output) — it
 * carries the engineering `categories[]` and the scalar `category` used for
 * RSN/Local structural matching.
 */
export interface BucketChannel {
  /** Engineering category strings (e.g. "Sports Channels", "HDTV Channels"). */
  categories?: string[];
  /** Scalar category used for structural rows ("local" / "rsn"). */
  category?: string;
  /**
   * Structural (zip/DMA) markers. RSN/Local rows are bucketed by the caller's
   * structural loop, never content-bucketed here — detected flags-first (the
   * scalar `category` can be clobbered by genre-pack injection).
   */
  isLocal?: boolean;
  isRsn?: boolean;
}

/**
 * Split comma-joined entries and normalise each for case-insensitive matching.
 * CMS may store multiple values in one entry ("Sports Channels, NBA") rather
 * than as separate array elements.
 */
const normaliseEngSet = (engCategories: string[] = []): string[] =>
  engCategories.flatMap(e => e.split(',').map(s => s.trim().toLowerCase())).filter(Boolean);

/**
 * Returns `{ [bucketKey]: channel[] }` — one entry per configured accordion that
 * has at least one matching channel. `bucketKey` resolves `label ?? key ?? value`
 * so the same helper serves both caller shapes: the interactive tool's blok
 * objects (`label`) and the lineup's gridCategory KVs (`key`/`value`). The grid
 * renders by `kv.key`, so for the lineup the bucket lands on `key` to match.
 */
export function bucketChannelsByCategory<T extends BucketChannel>(
  channels: T[],
  categoryConfig: ChannelCategoryConfig[]
): Record<string, T[]> {
  const buckets: Record<string, T[]> = {};
  for (const config of categoryConfig || []) {
    const engSet = normaliseEngSet(config.engCategories);
    // Slug fallback for the flag-OFF / degraded shape: those configs carry NO
    // engCategories, and their channels carry a `categoryId` slug (in `category`)
    // rather than prose `categories[]`. Match that slug against the config `key`,
    // mirroring `isChannelInAnyCategory`. WITHOUT this, the categorical view
    // renders empty whenever channels come from AEM — i.e. the `flag=FALSE`
    // rollback AND the flag-ON degraded fallback (DEEP-1 / DEEP-1b).
    const slugKey = engSet.length ? "" : String(config.key ?? "").trim().toLowerCase();
    if (!engSet.length && !slugKey) continue; // structural blok row (no eng, no key) — skip
    const bucketKey = config.label ?? config.key ?? config.value;
    if (!bucketKey) continue; // no accordion header to bucket under
    for (const channel of channels || []) {
      const scalarCat = (channel.category || "").trim().toLowerCase();
      // RSN/Local are zip/DMA structural rows the caller buckets separately; never
      // content-bucket them (the slug-path could otherwise double-bucket them under
      // an RSN/LOCAL config). Flags-FIRST to match chlupStore + the structural loop +
      // isLocalRsn everywhere: a structural channel whose scalar `category` was
      // clobbered (genre-pack injection) but still carries isLocal/isRsn is skipped.
      if (channel.isLocal || channel.isRsn || scalarCat === "local" || scalarCat === "rsn") continue;
      const matched = engSet.length
        ? (channel.categories || []).some(c => engSet.includes(c.trim().toLowerCase()))
        : scalarCat === slugKey;
      if (matched) (buckets[bucketKey] ??= []).push(channel);
    }
  }
  return buckets;
}

/**
 * CATEGORY-view multi-membership map for the compare-tool MODAL. The modal's DDS
 * provider rebuilds each Channel from a fixed whitelist (dropping categories[]),
 * so the lineup's on-channel multi-membership can't survive to the modal grid.
 * Build the SAME membership server-side via bucketChannelsByCategory and carry it
 * as a side map keyed by the id the grid sees: the provider sets
 * `channel.id = parseInt(channelId, 10)`, so values are parsed ints to match the
 * grid's `includes(chn.id)`. Structural Local/RSN are excluded (bucketChannelsByCategory
 * skips them; the grid's scalar-category path renders them); non-numeric ids drop.
 */
export function buildChannelCategoryMembership(
  channels: (BucketChannel & { channelId?: string | number })[] | undefined,
  categoryConfig: ChannelCategoryConfig[]
): Record<string, number[]> {
  const buckets = bucketChannelsByCategory(channels, categoryConfig);
  return Object.fromEntries(
    Object.entries(buckets).map(([bucketKey, chs]) => [
      bucketKey,
      chs.map(ch => parseInt(String(ch.channelId), 10)).filter(n => !Number.isNaN(n))
    ])
  );
}

/**
 * Resolve the SINGLE representative category bucket key for a channel — the
 * first configured accordion whose `engCategories` intersect the channel's
 * multi-valued `categories[]`. This is the per-channel companion to
 * `bucketChannelsByCategory`: the grid places a channel under EVERY matching
 * accordion (multi-membership), but the addon/hidden grouping and the client
 * search index need ONE stable key per channel, and it must be the SAME bucket
 * key the grid uses (`config.label ?? config.key ?? config.value`) so the
 * client's `allCategories.find(cat => cat.key === key)` lookup resolves.
 *
 * Dual-path (mirrors `channel-lineup-parser.ts`):
 *  - flag-ON  (channel carries `categories[]`): returns the first matching
 *    accordion's bucket key, or `fallbackCategoryId` when nothing matches
 *    (format-only channels like HDTV-only).
 *  - flag-OFF / AEM (no `categories[]`): returns `fallbackCategoryId` (the
 *    legacy single `categoryId` slug) untouched.
 *
 * The match is order-sensitive: pass the ALREADY-ORDERED category config (e.g.
 * the output of `orderChannelCategoryConfig`) so the representative key is the
 * highest-priority accordion the channel belongs to.
 */
export function resolveChannelCategoryKey(
  channelCategories: string[] | undefined,
  categoryConfig: ChannelCategoryConfig[],
  fallbackCategoryId: string | undefined
): string | undefined {
  const chCats = channelCategories ?? [];
  if (chCats.length) {
    for (const config of categoryConfig || []) {
      const engSet = normaliseEngSet(config.engCategories);
      if (!engSet.length) continue; // structural row (RSN/Local) — skip
      const bucketKey = config.label ?? config.key ?? config.value;
      if (!bucketKey) continue;
      if (chCats.some(c => engSet.includes(c.trim().toLowerCase()))) {
        return bucketKey;
      }
    }
  }
  // No `categories[]` (flag-OFF/AEM) or no configured match — keep the legacy
  // single-category slug so AEM bucketing and the ENTERTAINMENT default survive.
  return fallbackCategoryId;
}

/**
 * Returns true if a channel matches any configured accordion.
 *
 * Handles two shapes that `categories` can arrive as:
 *  - Blok shape (flag-ON): `{label, engCategories[]}` — matches via eng category strings
 *  - KV shape (flag-OFF / structural entries): `{key, value}` — matches via scalar category
 *
 * Structural channels (RSN/Local from the zip API) always return true so they
 * continue to appear in A-Z / Channel Number views regardless of shape.
 */
export function isChannelInAnyCategory(
  channel: BucketChannel,
  categoryConfig: ChannelCategoryConfig[]
): boolean {
  const scalarCat = (channel.category || "").trim().toLowerCase();
  if (scalarCat === "local" || scalarCat === "rsn") return true;
  const chCats: string[] = channel.categories || [];
  return (categoryConfig || []).some(config => {
    if (config.engCategories?.length) {
      const engSet = normaliseEngSet(config.engCategories);
      return chCats.some((c: string) => engSet.includes(c.trim().toLowerCase()));
    }
    // KV shape: match against the scalar category field (flag-OFF / structural)
    const key = config.key;
    return Boolean(key) && scalarCat === String(key).trim().toLowerCase();
  });
}

/**
 * Pick the Entertainment accordion ROW from a category config — the SINGLE
 * /entertainment/i row-selection shared by both Entertainment-fallback resolvers
 * below (DRY: the name-matching lives here, neither resolver re-implements it).
 *
 * Restrict the candidate rows with `predicate` so each resolver scopes the SAME
 * selection to the rows it cares about:
 *  - lineup / interactive grid → content rows only (engCategories present), the
 *    EXACT prior selection, so those callers stay byte-for-byte unchanged.
 *  - compare-tool modal → all rows (a content config OR a degraded label-only
 *    one), since the modal buckets by label and needs the row's label either way.
 *
 * Within the candidate set: prefer the row whose label/key matches
 * /entertainment/i; else the first candidate row.
 * ponytail: first row is the fallback — no separate "default" config field, add
 * one only if a non-Entertainment default is ever needed.
 */
const selectEntertainmentRow = (
  categoryConfig: ChannelCategoryConfig[],
  predicate: (c: ChannelCategoryConfig) => boolean
): ChannelCategoryConfig | undefined => {
  const rows = (categoryConfig || []).filter(predicate);
  return (
    rows.find(c => [c.label, c.key].some(v => /entertainment/i.test(v ?? ""))) ??
    rows[0]
  );
};

/**
 * Default content accordion VALUE for includable channels that match nothing.
 *
 * Some signature-packaged channels (e.g. "DIRECTV 4K Live") carry only FORMAT
 * categories ("4K", "Mixed Authorization") that no configured CONTENT accordion
 * matches, so `isChannelInAnyCategory` is false and all three signature views
 * (A-Z / Channel# / Categorical) drop them. We resolve the Entertainment
 * accordion from the SAME config the views match against and append its first
 * `engCategories` value so the existing gates pass UNCHANGED.
 *
 * Used by the surfaces that bucket on `categories[] ∩ engCategories`:
 * the lineup (via `applyEntertainmentCategoryFallback` below) and the interactive
 * tool (which calls this directly before `parseChannelsForV2`). Scoped to CONTENT
 * rows (engCategories present), so a label-only config yields `undefined` — the
 * unchanged prior contract. Returns the eng VALUE the matcher compares
 * (normaliseEngSet lowercases + splits on commas).
 */
export const resolveEntertainmentCategory = (
  categoryConfig: ChannelCategoryConfig[]
): string | undefined => {
  const entertainment = selectEntertainmentRow(categoryConfig, c => Boolean(c.engCategories?.length));
  return entertainment?.engCategories?.[0] ?? entertainment?.label;
};

/**
 * Default content accordion BUCKET KEY for the compare-tool MODAL.
 *
 * The modal's DDS provider buckets each channel by scalar `category` equality
 * against the accordion KEY (= `label`, since the modal config flattens to a
 * "label,label" CSV that `getKVsFromStrings` parses to `{key:label, value:label}`)
 * — NOT against `engCategories` like the lineup/interactive grid does. So a
 * format-only channel needs its scalar `categoryId` set to the Entertainment
 * accordion's bucket KEY, which is the SAME `label ?? key ?? value` that
 * `resolveChannelCategoryKey` returns for a matched channel. Shares the one
 * `selectEntertainmentRow` selection with `resolveEntertainmentCategory` (DRY);
 * considers ALL rows (the modal's flag-ON config carries engCategories, but the
 * resolver also works for a degraded label-only accordion config).
 */
export const resolveEntertainmentCategoryKey = (
  categoryConfig: ChannelCategoryConfig[]
): string | undefined => {
  const entertainment = selectEntertainmentRow(categoryConfig, () => true);
  return entertainment?.label ?? entertainment?.key ?? entertainment?.value;
};

/**
 * Default include-gate for the lineup: a channel gets the Entertainment fallback
 * only when it is INCLUDABLE (`isInclAny`), NOT MyFree and NOT signatureExcluded.
 * The structural (Local/RSN) and already-categorized skips are applied
 * universally by `applyEntertainmentCategoryFallback` regardless of predicate, so
 * this only expresses the lineup-shape inclusion fields.
 */
const lineupIncludeGate = (
  channel: BucketChannel & {
    isInclAny?: boolean;
    isMyFreeDTV?: boolean;
    signatureExcluded?: boolean;
  }
): boolean => Boolean(channel?.isInclAny) && !channel.isMyFreeDTV && !channel.signatureExcluded;

/**
 * Clone-on-write category fallback for the signature category views. For every
 * channel the `shouldInclude` predicate accepts — that is NOT structural
 * (Local/RSN) and whose `categories[]` matches no configured content accordion —
 * produce a shallow CLONE with the Entertainment accordion's value appended to
 * `categories[]` and the scalar `category` (an unmatched FORMAT value) overwritten
 * with it. All other channels pass through by reference.
 *
 * IMPORTANT: this does NOT mutate in place — it RETURNS a new array, and callers
 * MUST use the return value. The inputs may be deep-frozen channels-service cache
 * objects (`cache-client` deepFreeze), so an in-place write threw
 * `TypeError: Cannot assign to read only property 'categories'` and would corrupt
 * the shared cache across requests (WAT-11076).
 *
 * Shared by BOTH category-rendering surfaces that bucket on `categories[]`:
 *  - Lineup (`chlupStore`): assign the return to `data.channels` before the three
 *    builders run, so `isChannelInAnyCategory` returns true (A-Z / Channel# gates
 *    pass) and `bucketChannelsByCategory` places the channel under Entertainment
 *    (Categorical view). Uses the default `lineupIncludeGate` (`isInclAny`-based).
 *  - Interactive tool: reassign `sourceChannels` to the return before
 *    `parseChannelsForV2`. Those channels carry NO `isInclAny` (different shape),
 *    so the caller passes its own "is this an included channel" predicate; the
 *    cloned `categories[]` then flows into BOTH `resolveChannelCategoryKey`
 *    (matches Entertainment) AND `bucketChannelsByCategory` (buckets under it).
 *
 * Structural (Local/RSN) and already-categorized channels are returned untouched
 * (same reference).
 *
 * @param shouldInclude Optional per-channel gate; defaults to the lineup's
 *   `isInclAny && !MyFree && !signatureExcluded`, so the existing lineup call is
 *   unchanged. The structural + already-categorized skips run in all cases.
 * @returns A NEW array (clones only the channels that received the fallback).
 */
export function applyEntertainmentCategoryFallback<
  T extends BucketChannel & {
    isInclAny?: boolean;
    isMyFreeDTV?: boolean;
    signatureExcluded?: boolean;
  }
>(
  channels: T[] | undefined,
  categoryConfig: ChannelCategoryConfig[],
  shouldInclude: (channel: T) => boolean = lineupIncludeGate
): T[] {
  const list = channels ?? [];
  const fallback = resolveEntertainmentCategory(categoryConfig);
  if (!fallback) return list; // no content accordion configured — nothing to do
  // CLONE-ON-WRITE (WAT-11076): callers pass channels that may be the deep-frozen
  // channels-service cache envelope (cache-client deepFreeze). Mutating `channel.categories`
  // in place threw `TypeError: Cannot assign to read only property 'categories'` on the
  // flag-ON 200 branch — the channelsInteractiveToolAdapterV2 catch then returned null and
  // the whole component was dropped. Even unfrozen, in-place mutation of a cached object
  // corrupts it for every later request. So return a NEW array: only the channels needing
  // the fallback are replaced with shallow clones (fresh categories[]); the rest pass
  // through by reference. Callers MUST use the returned array (not the input).
  return list.map(channel => {
    if (
      channel?.isLocal ||
      channel?.isRsn ||
      !shouldInclude(channel) ||
      isChannelInAnyCategory(channel, categoryConfig)
    ) {
      return channel;
    }
    return {
      ...channel,
      categories: [...(channel.categories ?? []), fallback],
      // Overwrite the scalar too: it currently holds an unmatched FORMAT value
      // (e.g. "4K"), so the :257/:357 empty-skip AND the :262/:382 scalar sort
      // both reflect the Entertainment bucket the channel now lands in.
      category: fallback
    };
  });
}

/**
 * Union the stream + satellite service channel lists into one row per channel,
 * keyed by `channelId` (the platform-independent CCID; falls back to the display
 * label only when an id is absent). A channel carried on BOTH platforms is
 * MERGED — its per-platform package memberships (packageListSystem /
 * addonPackageList / promoPackageList) are unioned — so the both-platform compare
 * modal lights up the stream AND satellite tier columns for it. Keeping only the
 * first entry (the prior dedupe-by-label behavior) silently dropped the second
 * platform's memberships, blanking that channel's columns for the other platform.
 * The base row is a shallow copy so the cached service envelopes are never
 * mutated across requests.
 */
export const mergeChannelsAcrossPlatforms = (
  ...sources: (any[] | undefined)[]
): any[] => {
  const byKey = new Map<string, any>();
  const order: string[] = [];
  const unionLists = (a?: any[], b?: any[]) =>
    Array.from(new Set([...(a ?? []), ...(b ?? [])]));
  for (const channel of sources.flatMap(source => source ?? [])) {
    const key = channel?.channelId || channel?.channelNameLabel;
    if (!key) continue;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...channel });
      order.push(key);
      continue;
    }
    existing.packageListSystem = unionLists(existing.packageListSystem, channel.packageListSystem);
    existing.addonPackageList = unionLists(existing.addonPackageList, channel.addonPackageList);
    existing.promoPackageList = unionLists(existing.promoPackageList, channel.promoPackageList);
  }
  return order.map(key => byKey.get(key));
};

/* ════ 7. Category ordering (zip/DMA rows first) ═════════════════════════ */

/**
 * Shared channel-category ordering for the lineup-family tools that consume the
 * package-config `categoryConfig` (`{ label, engCategories[] }`): the interactive
 * tool and the compare tool. It orders categories to match the channel-lineup —
 * zip/DMA rows first (Local channels → Regional Sports), then National Sports,
 * then the remaining content categories in package-config order.
 *
 * The /channel-lineup + /satellite page itself does NOT use this: it hand-builds
 * its KV array (injecting RSN/LOCAL as structural rows separately from the
 * content categories), so it expresses the same order in that literal array.
 */

/**
 * Canonical structural key for a zip/DMA-sourced row (Regional Sports / Local
 * channels) — identified by the absence of `engCategories` plus a label match.
 * Content rows (matched by engCategories) return null and keep `label === key`.
 */
export const structuralCategoryKey = (category: any): string | null => {
  if (category?.engCategories?.length) return null;
  const label = category?.label?.toLowerCase() ?? "";
  if (label.includes("local")) return "LOCAL";
  if (label.includes("regional sports")) return "RSN";
  return null;
};

/**
 * Order so the zip/DMA rows lead: Local (0) → Regional Sports (1) → National
 * Sports (2 — the content row whose engCategories mention "sport") → everything
 * else (3, kept in package-config order via a stable sort). Only reorders when
 * the config carries engCategories (flag-ON); a flag-OFF / AEM config (no
 * engCategories) is returned untouched, preserving prod behavior.
 */
export const orderChannelCategoryConfig = (categoryConfig: any[] = []): any[] => {
  const rank = (category: any): number => {
    const key = structuralCategoryKey(category);
    if (key === "LOCAL") return 0;
    if (key === "RSN") return 1;
    if (category?.engCategories?.some((e: string) => e.toLowerCase().includes("sport"))) return 2;
    return 3;
  };
  const hasEngCategories = categoryConfig.some((c: any) => c?.engCategories?.length);
  return hasEngCategories
    ? [...categoryConfig].sort((a, b) => rank(a) - rank(b))
    : categoryConfig;
};

/* ════ 8. FAST-channel filter (satellite display) ════════════════════════ */

/**
 * Satellite FAST-channel display filter (WAT-10783 / WAT-10810).
 *
 * FAST (free ad-supported streaming) channels are online / in-app only — they
 * are NOT receivable on a DIRECTV Satellite package. channels-service flags them
 * on the SATELLITE feed with `fastChannel: true` (the single source of truth —
 * WAT-10808; computed from the `"FAST Channels"` category OR the `DTVE FAST_ONLY`
 * package). UF excludes them from Satellite CHLUP displays and from any channel
 * count derived off those displays.
 *
 * This is a DISPLAY-LAYER filter (AC-3c) — it never mutates the underlying feed.
 * Scope: SATELLITE context ONLY (streaming/internet unchanged). Callers pass
 * their existing satellite gate as `isSatelliteContext`. The flag is read
 * directly off the channel — NOT derived from `channelType`. A channel with no
 * `fastChannel` attribute is treated as non-FAST and kept (`=== true` handles
 * absent/false identically).
 */
export interface FastChannelFlagged {
  fastChannel?: boolean;
}

/**
 * True when `channel` must be EXCLUDED from a Satellite display.
 *
 * @param channel            A channel record (raw v4 or adapted listing entry).
 * @param isSatelliteContext The caller's existing satellite gate. When falsy
 *                           (stream/internet display), always returns false so
 *                           streaming behavior is untouched.
 */
export function isExcludedFastChannel(
  channel: FastChannelFlagged | null | undefined,
  isSatelliteContext: boolean | undefined
): boolean {
  return Boolean(isSatelliteContext) && channel?.fastChannel === true;
}

/* ════ 9. MyHomeTeam overlay (Storyblok feed onto OTT envelope) ══════════ */

/**
 * Overlay the MyHomeTeam out-of-market RSN channels onto a channels-service OTT
 * envelope.
 *
 * The MyHomeTeam RSNs are a Storyblok feed (`myHomeTeamOutOfMarketRsnChannels`),
 * NOT part of the channels-service payload. The AEM channel list already merges
 * them (`get-channel-listing-page.aem.ts` -> `addMyHomeTeamOutOfMarketRSNs`), but
 * flag-ON consumers read the channels-service envelope and `pickChannels` returns
 * the service list whenever it is non-empty — silently dropping the AEM-only
 * overlay. Applying the same overlay to the OTT envelope at populate time makes
 * the MyHomeTeam genre pack render in BOTH flag states.
 *
 * - Immutable: returns a new envelope; never mutates `ott` or its `channels`.
 * - Flag-OFF safe: when the service is absent (`ott` is undefined/null) the value
 *   is returned untouched, so AEM remains the single overlay source on that path.
 * - OTT-only: satellite has no channels-service overlay and MyHomeTeam is an
 *   `ATT_TV_` (stream) genre.
 *
 * @param ott  channels-service OTT envelope `{ platform, lastUpdated, channels[] }`, or undefined flag-OFF
 * @param rsns MyHomeTeam out-of-market RSN channels from the Storyblok feed
 * @returns the envelope with the RSNs appended to `channels`, or `ott` unchanged when absent
 */
export const overlayMyHomeTeamRsns = (ott: any, rsns: any[]) =>
  ott ? { ...ott, channels: [...(ott?.channels ?? []), ...(rsns ?? [])] } : ott;
